import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Notification from '../../components/common/Notification/Notification';
import { confirmEmail, resendConfirmation } from '../../api/auth';
import { useResendTimer } from '../../hooks/useResendTimer';

const EmailConfirmation: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'idle'>('idle');
  const [message, setMessage] = useState<string>('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  
  const { isDisabled, startTimer, buttonText } = useResendTimer(resendEmail || '');

  useEffect(() => {
    if (token) {
      handleConfirmation(token);
    } else {
      setStatus('error');
      setMessage('Invalid confirmation link. No token provided.');
    }
  }, [token]);

  const handleConfirmation = async (confirmationToken: string) => {
    setStatus('loading');
    try {
      await confirmEmail(confirmationToken);
      setStatus('success');
      setMessage('Email confirmed! You can now log in.');
    } catch (err: any) {
      setStatus('error');
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to confirm email';
      setMessage(errorMessage);
    }
  };

  const handleResendConfirmation = async () => {
    if (!resendEmail) {
      setResendMessage('Please enter your email address');
      return;
    }

    setResendLoading(true);
    setResendMessage(null);
    try {
      await resendConfirmation(resendEmail);
      setResendMessage('Confirmation email sent successfully!');
      startTimer(); // Start 60-second countdown
    } catch (err: any) {
      setResendMessage(err.response?.data?.detail || err.message || 'Failed to send confirmation email');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <PageContainer>
      <div className="max-w-md mx-auto mt-10">
        <Card>
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Email Confirmation</h2>
          </div>

          {status === 'loading' && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
              <p className="text-gray-600">Confirming your email...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="space-y-4">
              <Notification
                type="success"
                message={message}
                onClose={() => {}}
              />
              <div className="text-center">
                <Button
                  onClick={() => navigate('/login')}
                  fullWidth
                >
                  Go to Login
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <Notification
                type="error"
                message={message}
                onClose={() => {}}
              />
              <div className="space-y-4">
                <div>
                  <label htmlFor="resend-email" className="block text-sm font-medium text-gray-700 mb-2">
                    Enter your email to resend confirmation:
                  </label>
                  <input
                    id="resend-email"
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                    placeholder="your@email.com"
                  />
                </div>
                <Button
                  onClick={handleResendConfirmation}
                  isLoading={resendLoading}
                  disabled={isDisabled || resendLoading}
                  fullWidth
                >
                  {buttonText}
                </Button>
                {resendMessage && (
                  <Notification
                    type={resendMessage.includes('successfully') ? 'success' : 'error'}
                    message={resendMessage}
                    onClose={() => setResendMessage(null)}
                  />
                )}
                <div className="text-center">
                  <Button
                    onClick={() => navigate('/login')}
                    variant="secondary"
                    fullWidth
                  >
                    Go to Login
                  </Button>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </PageContainer>
  );
};

export default EmailConfirmation;

